import { useState, useEffect } from "react";
import { ArrowRight, Truck, Clock, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { useHomeBanners } from "@/hooks/queries";
import { BannerType } from "@/types/banner.dto";
import { resolveUploadUrl } from "@/services/filesService";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const defaultSlides = [
  {
    title: "Fresh from your local stores to your doorstep",
    subtitle: "Shop from trusted neighbourhood stores. Get groceries, essentials, and more delivered in minutes.",
    imageUrl: "/images/hero-banner.jpg",
    linkUrl: "/products",
    badge: "🚀 Delivering fresh groceries"
  },
  {
    title: "Trendy fashion & styling from local boutiques",
    subtitle: "Explore the latest apparel, accessories, and traditional clothing from trusted shops near you.",
    imageUrl: "/images/fashion-banner.jpg",
    linkUrl: "/products",
    badge: "🛍️ Local boutique fashion"
  },
  {
    title: "Latest electronics & appliances at best prices",
    subtitle: "Upgrade your gadgets with smartphones, accessories, and home appliances delivered safely to you.",
    imageUrl: "/images/electronics-banner.jpg",
    linkUrl: "/products",
    badge: "⚡ Best deals on electronics"
  }
];

const HeroSection = () => {
  const { data, isLoading } = useHomeBanners({ type: BannerType.Home, activeOnly: true, pageSize: 5 });
  const banners = [...(data?.items ?? [])].sort((a, b) => a.displayOrder - b.displayOrder);

  const slides = banners.length > 0
    ? banners.map((b) => ({
        title: b.title,
        subtitle: b.subtitle,
        imageUrl: resolveUploadUrl(b.imageUrl),
        linkUrl: b.linkUrl || "/products",
        badge: "🔥 Hot Deal Today"
      }))
    : defaultSlides;

  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  return (
    <section className="relative overflow-hidden bg-emerald-50/30 dark:bg-emerald-950/10 pt-4 pb-8 md:py-12 lg:py-16">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left: Text Content Column */}
          <div className="lg:col-span-6 flex flex-col justify-center min-h-[320px] sm:min-h-[380px] lg:min-h-[400px] order-2 lg:order-1">
            <div className="relative w-full">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  className="space-y-5 flex flex-col items-center lg:items-start text-center lg:text-left"
                >
                  <span className="inline-block px-3.5 py-1.5 rounded-full bg-primary/10 text-primary text-xs sm:text-sm font-medium border border-primary/20 shadow-sm">
                    {slides[currentSlide].badge}
                  </span>
                  
                  <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold leading-[1.12] text-foreground tracking-tight">
                    {slides[currentSlide].title}
                  </h1>
                  
                  <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-lg leading-relaxed">
                    {slides[currentSlide].subtitle}
                  </p>
                </motion.div>
              </AnimatePresence>

              {/* Actions & stats remain static so they don't shift during transitions */}
              <div className="mt-8 flex flex-col items-center lg:items-start w-full space-y-8">
                <div className="flex flex-wrap justify-center lg:justify-start gap-3 w-full">
                  <Link to={slides[currentSlide].linkUrl.startsWith("http") ? "/products" : slides[currentSlide].linkUrl}>
                    <Button className="h-12 px-6 bg-gradient-primary hover:opacity-90 text-primary-foreground font-heading font-semibold rounded-xl text-base shadow-glow transition-all duration-200">
                      Shop Now <ArrowRight className="w-5 h-5 ml-2 animate-pulse" />
                    </Button>
                  </Link>
                  <Link to="/stores">
                    <Button
                      variant="outline"
                      className="h-12 px-6 border-border text-foreground hover:bg-secondary font-heading font-semibold rounded-xl text-base transition-colors duration-200"
                    >
                      Explore Stores
                    </Button>
                  </Link>
                </div>

                <div className="flex flex-wrap justify-center lg:justify-start gap-4 w-full">
                  {[
                    { icon: Truck, label: "Fast Delivery", value: "15-30 min" },
                    { icon: Clock, label: "Open Daily", value: "7am - 11pm" },
                    { icon: Shield, label: "Secure Payments", value: "100% Safe" },
                  ].map((stat) => (
                    <div
                      key={stat.label}
                      className="flex items-center gap-3 bg-secondary/50 backdrop-blur-sm rounded-xl px-4 py-3 border border-border/50 transition-colors hover:bg-secondary/80"
                    >
                      <stat.icon className="w-5 h-5 text-primary" />
                      <div className="text-left">
                        <p className="text-[10px] sm:text-xs text-muted-foreground font-medium uppercase tracking-wider">{stat.label}</p>
                        <p className="text-xs sm:text-sm font-heading font-bold text-foreground">{stat.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right: Image Slider Column */}
          <div className="lg:col-span-6 w-full order-1 lg:order-2">
            <div className="relative w-full aspect-[2/1] sm:aspect-[16/10] lg:aspect-[4/3] xl:aspect-[16/10] overflow-hidden rounded-2xl md:rounded-3xl shadow-xl border border-border/40 bg-secondary">
              {isLoading ? (
                <Skeleton className="w-full h-full" />
              ) : (
                <div className="absolute inset-0 w-full h-full">
                  <AnimatePresence mode="popLayout">
                    <motion.img
                      key={currentSlide}
                      src={slides[currentSlide].imageUrl}
                      alt={slides[currentSlide].title}
                      initial={{ opacity: 0, scale: 1.02 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.98 }}
                      transition={{ duration: 0.5, ease: "easeInOut" }}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </AnimatePresence>
                  
                  {/* Subtle vignette/gradient overlay to add depth */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                </div>
              )}

              {/* Dot Indicators */}
              {!isLoading && slides.length > 1 && (
                <div className="absolute bottom-3 sm:bottom-5 left-1/2 -translate-x-1/2 flex gap-2 z-20 bg-black/20 backdrop-blur-md px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-full border border-white/10">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={cn(
                        "w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full transition-all duration-300",
                        currentSlide === index
                          ? "bg-primary w-5 sm:w-6"
                          : "bg-white/60 hover:bg-white"
                      )}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
